import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryClient';
import { useAuth } from '../pages/Auth/AuthProvider';
import { syncSubscription, type PaymentIssue } from '../services/subscriptionService';

interface SubscriptionSyncContextType {
  paymentIssue: PaymentIssue | null;
  dismissed: boolean;
  dismiss: () => void;
}

const SubscriptionSyncContext = createContext<SubscriptionSyncContextType>({
  paymentIssue: null,
  dismissed: false,
  dismiss: () => {},
});

// Only hit Polar once per user per window across reloads/tabs.
const SYNC_THROTTLE_MS = 5 * 60 * 1000;
const lastSyncKey = (uid: string) => `ss:lastSubSync:${uid}`;
const issueKey = (uid: string) => `ss:subIssue:${uid}`;
const dismissKey = (uid: string) => `ss:subIssueDismissed:${uid}`;

/**
 * Runs a subscription sync on login (throttled, non-blocking) so a stale DB row
 * self-heals against Polar, and surfaces the live payment status for the
 * past-due banner. Must be mounted inside AuthProvider and QueryClientProvider.
 */
export function SubscriptionSyncProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user, refreshSession } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id ?? null;

  const [paymentIssue, setPaymentIssue] = useState<PaymentIssue | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const handledRef = useRef<string | null>(null);

  const dismiss = useCallback(() => {
    setDismissed(true);
    if (userId) sessionStorage.setItem(dismissKey(userId), 'true');
  }, [userId]);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      handledRef.current = null;
      setPaymentIssue(null);
      setDismissed(false);
      return;
    }
    // Run once per user per mount.
    if (handledRef.current === userId) return;
    handledRef.current = userId;

    // Hydrate the banner immediately from the last known state (survives reloads
    // within the throttle window, when we skip the network call).
    setDismissed(sessionStorage.getItem(dismissKey(userId)) === 'true');
    try {
      const cached = sessionStorage.getItem(issueKey(userId));
      if (cached) setPaymentIssue(JSON.parse(cached) as PaymentIssue);
    } catch {
      /* ignore malformed cache */
    }

    const last = Number(localStorage.getItem(lastSyncKey(userId)) || 0);
    if (Date.now() - last < SYNC_THROTTLE_MS) return; // synced recently — skip network
    localStorage.setItem(lastSyncKey(userId), String(Date.now()));

    let cancelled = false;
    syncSubscription()
      .then(async (result) => {
        if (cancelled) return;
        const issue = result.paymentIssue ?? null;
        setPaymentIssue(issue);
        if (issue) sessionStorage.setItem(issueKey(userId), JSON.stringify(issue));
        else sessionStorage.removeItem(issueKey(userId));
        // Reflect the reconciled tier everywhere: refresh the session (additionalFields)
        // and the cached subscription info.
        await refreshSession();
        queryClient.invalidateQueries({ queryKey: queryKeys.subscription.info() });
      })
      .catch(() => {
        /* self-heal is best-effort; never block or surface an error to the user */
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, userId, refreshSession, queryClient]);

  return (
    <SubscriptionSyncContext.Provider value={{ paymentIssue, dismissed, dismiss }}>
      {children}
    </SubscriptionSyncContext.Provider>
  );
}

export function useSubscriptionSync() {
  return useContext(SubscriptionSyncContext);
}
